/**
 * Asset Domain Definition (domain-def@4) — the typed inventory.
 *
 * `Asset` (domain-model.md §3.5) — certificates, references, portraits,
 * portfolios (Nachweise / Anlagen). Assets are `EvidenceRef` sources
 * (`AssetRef`, in primitives) and application enclosures. A `Document` uploaded
 * at onboarding may be promoted to an `Asset`.
 *
 * Stream: `Agg::career::{userId}::Asset::{assetId}`.
 */

import type { DomainDefinitionJSON } from "./schema"

const assetDomain = {
  schemaVersion: "domain-def@4",
  name: "Asset",
  version: "1.0.0",
  description:
    "Asset — typed, versioned inventory (certificates, references, diplomas, portraits, portfolio) used as evidence sources and application enclosures",

  types: {
    AssetKind:
      "'certificate' | 'reference' | 'diploma' | 'work_certificate' | 'portrait' | 'portfolio' | 'other'",

    Asset: {
      "+": "reject",
      id: "Urn",
      owner: "UserId",
      kind: "AssetKind",
      /** Confirmation lifecycle (reuses the ledger vocabulary): a proposed asset
       *  waits for the user; a directly-added asset is `confirmed` by definition. */
      status: "ClaimStatus",
      /** Versioned, first-class */
      version: "number.integer >= 1",
      /** The binary lives in the object store */
      file: "ObjectRef",
      title: "NonEmptyString",
      "issuer?": "string",
      "issuedAt?": "Timestamp",
      "validThrough?": "Timestamp",
      /** Claims this asset substantiates */
      evidenceClaims: "Urn[]",
      /** Set when proposed from an uploaded Document — its ObjectRef is reused, no re-upload */
      "sourceDocumentId?": "Urn",
      createdAt: "Timestamp",
      updatedAt: "Timestamp",
    },

    // ─── Event payloads ──────────────────────────────────────────

    AssetAddedPayload: {
      "+": "reject",
      owner: "UserId",
      asset: "Asset",
    },

    AssetUpdatedPayload: {
      "+": "reject",
      owner: "UserId",
      assetId: "Urn",
      version: "number.integer >= 1",
      asset: "Asset",
    },

    /**
     * A Document uploaded at onboarding was promoted to a PROPOSED enclosure
     * Asset. Carries the full proposed `asset` (status `proposed`,
     * `sourceDocumentId` = `documentId`); `documentId` kept for provenance.
     */
    AssetPromotedFromDocumentPayload: {
      "+": "reject",
      owner: "UserId",
      asset: "Asset",
      documentId: "Urn",
    },

    /** The user confirmed a proposed asset classification → usable inventory enclosure */
    AssetConfirmedPayload: {
      "+": "reject",
      owner: "UserId",
      assetId: "Urn",
      confirmedAt: "Timestamp",
    },

    AssetRemovedPayload: {
      "+": "reject",
      owner: "UserId",
      assetId: "Urn",
      "reason?": "string",
    },
  },

  resources: {
    asset: {
      description: "A single typed asset in the owner's inventory",
      type: "Asset",
      urnPattern: "urn:career:asset:{assetId}",
      fetch: { method: "GET", urlTemplate: "/api/v1/users/{userId}/assets/{assetId}" },
      authz: { visibility: "user-owned", scopes: ["asset:read"] },
    },
  },

  eventStream: {
    streamPattern: "Agg::career::{userId}::Asset::{assetId}",
    events: {
      "asset.Added": {
        description: "A new asset was added to the inventory",
        type: "AssetAddedPayload",
      },
      "asset.Updated": {
        description: "An asset's metadata or file version was updated",
        type: "AssetUpdatedPayload",
      },
      "asset.PromotedFromDocument": {
        description: "An uploaded onboarding document was promoted to a proposed enclosure asset",
        type: "AssetPromotedFromDocumentPayload",
      },
      "asset.Confirmed": {
        description: "The owner confirmed a proposed asset classification — it may now be used as an enclosure",
        type: "AssetConfirmedPayload",
      },
      "asset.Removed": {
        description: "An asset was removed from the inventory (tombstone)",
        type: "AssetRemovedPayload",
      },
    },
  },

  authz: {
    scopes: {
      "asset:read": { description: "Read the owner's asset inventory" },
      "asset:write": { description: "Add, update, promote, or remove assets" },
    },
    policies: {
      "owner-only": {
        kind: "owner",
        description: "Assets are owner-scoped to the calling Subject (law C1)",
      },
    },
    defaults: {
      unauthenticatedAccess: "deny",
      missingRolesBehavior: "deny",
    },
  },
} as const satisfies DomainDefinitionJSON

export default assetDomain
